import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAPI } from '@providers/useAPI';
import { GetOrgMembersMemberResponseDto, SubscriptionsService } from 'app/API';
import { debounce } from 'lodash-es';
import React, { useEffect } from 'react';
import { Content } from './styles/Content';

type TProps = {
  baId: string;
  subId: string;
  onCancel: () => void;
  onSuccess: (assignee: GetOrgMembersMemberResponseDto | null) => void;
};

export const AddUserLicenseModal = ({ onCancel, onSuccess, baId, subId }: TProps) => {
  const { call } = useAPI();
  const [users, setUsers] = React.useState<GetOrgMembersMemberResponseDto[]>([]);
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<GetOrgMembersMemberResponseDto | null>(null);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      call(
        SubscriptionsService.getOrgMembers({
          baId,
          subscriptionId: subId,
          orderBy: 'name',
          orderType: 'asc',
          pageNo: '1',
          pageSize: '10000',
          search,
        }),
      ).then((result) => {
        setUsers(result.data);
      });
    }, 500);

    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [search]);

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    onSuccess(assignee);
  };

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Add License to User</ModalTitle>
      <Content>
        {renderAssignee()}
        <Input
          className="assignee-input"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />
        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.name}
                    </p>
                  </Td>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.email}
                    </p>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={!assignee} onClick={handleSubmit} data-testid="add-user-license-button">
            Add User
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
